import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms'
import { AvailityServiceReview, InsuranceProviderConfiguration, InsuranceProviderConfigurationModel, PatientSexes } from '@mmx/shared'
import { Subscription } from 'rxjs'

import {
  getConditionComparators,
  getDependantControl,
  getLocationControls,
  getNameControls,
  removeIrrelevantControls,
} from './auth-form-build-helpers'

export class AuthForm {
  public form: FormGroup
  public procedures: FormArray
  public renderingProviders: FormArray
  public providerNotes: FormArray
  public diagnoses: FormArray
  public additionalServiceTypes: FormArray
  public codeValueMap: Record<string, AvailityServiceReview.CodeValue[]> = {}
  public patientCodeValueMap: Record<string, AvailityServiceReview.CodeValue[]> = {}
  public proceduresCodeValueMap: Record<string, AvailityServiceReview.CodeValue[]> = {}
  public additionalServiceTypesCodeValues: AvailityServiceReview.CodeValue[] = []
  public otherRenderingProviderRoleCodes: AvailityServiceReview.CodeValue[] = []

  public get facilityForm() {
    return this.renderingProviders.controls[0] as FormGroup
  }

  public get renderingProviderForm(): FormGroup {
    return this.renderingProviders.controls[1] as FormGroup
  }

  public get canAddProcedure(): boolean {
    if (this.configuration.elements.procedures) {
      if (this.configuration.elements.procedures.maxRepeats) {
        return this.procedures.controls.length < this.configuration.elements.procedures.maxRepeats
      }

      return true
    }

    return false
  }

  public get canAddProviderNote(): boolean {
    if (this.configuration.elements.providerNotes) {
      if (this.configuration.elements.providerNotes.maxRepeats) {
        return this.providerNotes.controls.length < this.configuration.elements.providerNotes.maxRepeats
      }

      return true
    }

    return false
  }

  public get canAddDiagnosis(): boolean {
    if (this.configuration.elements.diagnoses) {
      if (this.configuration.elements.diagnoses?.maxRepeats) {
        return this.diagnoses.controls.length < this.configuration.elements.diagnoses?.maxRepeats
      }

      return true
    }

    return false
  }

  public get canAddAdditionalServiceTypes(): boolean {
    if (this.configuration.elements.additionalServiceTypes) {
      if (this.configuration.elements.additionalServiceTypes.maxRepeats) {
        return this.additionalServiceTypes.controls.length < this.configuration.elements.additionalServiceTypes?.maxRepeats
      }

      return true
    }

    return false
  }

  private subscriptions: Subscription[] = []

  constructor(private configuration: InsuranceProviderConfigurationModel) {
    this.initForm()
  }

  public addProcedureRow() {
    this.procedures.push(this.getProcedureFormGroup())
  }

  public deleteProcedure(index: number) {
    this.procedures.removeAt(index)
  }

  public addProviderNoteRow() {
    this.providerNotes.push(this.getProviderNoteFormGroup())
  }

  public deleteProviderNote(index: number) {
    this.providerNotes.removeAt(index)
  }

  public addDiagnosesRow() {
    const formGroup = this.getDiagnosisFormGroup()
    const qualifierCode = !this.diagnoses.length ? AvailityServiceReview.principalDiagnosisQualifierCode :
      AvailityServiceReview.icd10DiagnosisQualifierCode

    formGroup.patchValue({
      qualifierCode,
    })

    this.diagnoses.push(formGroup)
  }

  public deleteDiagnosis(index: number) {
    this.diagnoses.removeAt(index)
  }

  public addAdditionalServiceTypeRow() {
    this.additionalServiceTypes.push(this.getAdditionalServiceTypeFormGroup())
  }

  public deleteAdditionalServiceType(index: number) {
    this.additionalServiceTypes.removeAt(index)
  }

  public destroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe()
    }
  }

  public coerceSex(value: PatientSexes): string {
    const codes = this.patientCodeValueMap.genderCode
    if (!codes) {
      return ''
    }

    // who knows, maybe other options/conditions needed
    const maleOptions = new Set(['Male'])
    const femaleOptions = new Set(['Female'])

    switch (value) {
      case 'M':
        return codes.find((codeValue) => maleOptions.has(codeValue.value)).code
      case 'F':
        return codes.find((codeValue) => femaleOptions.has(codeValue.value)).code
      default:
        return codes.find((codeValue) => !maleOptions.has(codeValue.value) &&
          !femaleOptions.has(codeValue.value)).code
    }
  }

  private initForm() {
    this.procedures = new FormArray([])
    this.renderingProviders = new FormArray([])
    this.providerNotes = new FormArray([])
    this.diagnoses = new FormArray([])
    this.additionalServiceTypes = new FormArray([])

    const requestingProvider = new FormGroup({
      ...getNameControls(),
      ...getLocationControls(),
      // add explicit required validator instead of ugly requiredFieldCombinations handling
      npi: new FormControl('', Validators.required),
      taxId: new FormControl('', Validators.required),
      payerAssignedProviderId: new FormControl(),
      specialtyCode: new FormControl(),
      contactName: new FormControl(),
      phone: new FormControl(),
      extension: new FormControl(),
      fax: new FormControl(),
      emailAddress: new FormControl(),
      submitterId: new FormControl(),
      url: new FormControl(),
    })
    const subscriber = new FormGroup({
      ...getNameControls(),
      ...getLocationControls(),
      memberId: new FormControl(),
    })
    const patient = new FormGroup({
      ...getNameControls(),
      ...getLocationControls(),
      subscriberRelationshipCode: new FormControl(),
      birthDate: new FormControl(),
      genderCode: new FormControl(),
      statusCode: new FormControl(),
      conditionCode: new FormControl(),
      medicareCoverage: new FormControl(),
      prognosisCode: new FormControl(),
    })

    this.form = new FormGroup({
      requestingProvider,
      subscriber,
      patient,
      diagnoses: this.diagnoses,
      requestTypeCode: new FormControl(),
      serviceTypeCode: new FormControl(),
      additionalServiceTypes: this.additionalServiceTypes,
      placeOfServiceCode: new FormControl(),
      serviceLevelCode: new FormControl(),
      fromDate: new FormControl(),
      toDate: new FormControl(),
      quantity: new FormControl(),
      quantityTypeCode: new FormControl(),
      admissionTypeCode: new FormControl(),
      admissionSourceCode: new FormControl(),
      chiropracticTreatmentCount: new FormControl(),
      procedures: this.procedures,
      renderingProviders: this.renderingProviders,
      providerNotes: this.providerNotes,

      // the fields below are exempted from the form since we never meet conditions when we need those
      // but they still could be easily included if we will need them in future

      // nursingHomeResidentialStatusCode: new FormControl(),
      // homeHealthStartDate: new FormControl(),
      // homeHealthCertificationPeriodStartDate: new FormControl(),
      // homeHealthCertificationPeriodEndDate: new FormControl(),
      // beginningSubluxationLevelCode: new FormControl(),
      // endingSubluxationLevelCode: new FormControl(),
      // spinalConditionCode: new FormControl(),
      // spinalConditionDescription: new FormControl(),
      // oxygenDeliverySystemTypeCode: new FormControl(),
      // oxygenFlowRate: new FormControl(),
      // oxygenDailyUseCount: new FormControl(),
      // oxygenUsePeriodHourCount: new FormControl(),
      // oxygenOrderText: new FormControl(),
      // oxygenEquipmentTypeCode: new FormControl(),
      // transportTypeCode: new FormControl(),
      // transportDistance: new FormControl(),
      // transportPurpose: new FormControl(),
      // transportLocations: new FormArray([]),
    })

    removeIrrelevantControls(this.form, this.configuration.elements)

    this.initValueMaps()

    this.initStateAndValidators(this.form, this.configuration.elements)
    this.initStateAndValidators(requestingProvider, this.configuration.elements.requestingProvider.elements)
    this.initStateAndValidators(subscriber, this.configuration.elements.subscriber.elements)
    this.initStateAndValidators(patient, this.configuration.elements.patient.elements, this.patientCodeValueMap)

    patient.patchValue({
      subscriberRelationshipCode: this.patientCodeValueMap.subscriberRelationshipCode?.find((cv) => cv.value === 'Self')?.code,
    })
    this.form.patchValue({
      requestTypeCode: this.codeValueMap.requestTypeCode?.[0].code,
    })

    // add required procedure row
    this.addProcedureRow()

    // add required diagnosis row
    this.addDiagnosesRow()

    // add one facility row and one rendering provider
    this.renderingProviders.push(this.getRenderingProviderFormGroup())
    this.renderingProviders.push(this.getRenderingProviderFormGroup())
  }

  private initStateAndValidators(
    formGroup: FormGroup,
    elements: InsuranceProviderConfiguration.Elements,
    codeValueMap?: Record<string, AvailityServiceReview.CodeValue[]>,
  ) {
    for (const key in formGroup.controls) {
      const element = elements[key]
      const control = formGroup.controls[key]

      if (!element.allowed) {
        control.disable()
      }

      if (element.allowedWhen) {
        for (const prop in element.allowedWhen) {
          const dependant = getDependantControl(prop, control)
          if (dependant) {
            const comparators = getConditionComparators(element.allowedWhen[prop])
            const subscription = dependant.valueChanges.subscribe((value) => {
              const compareResult = comparators.some((comparator) => !!comparator(value))
              if (compareResult) {
                control.enable()
              } else {
                control.disable()
              }

              control.updateValueAndValidity()
            })

            this.subscriptions.push(subscription)
          }
        }
      }

      if (element.notAllowedWhen) {
        for (const prop in element.notAllowedWhen) {
          const dependant = getDependantControl(prop, control)
          if (dependant) {
            const comparators = getConditionComparators(element.notAllowedWhen[prop])
            const subscription = dependant.valueChanges.subscribe((value) => {
              const compareResult = comparators.some((comparator) => !!comparator(value))
              if (compareResult) {
                control.disable()
              } else {
                control.enable()
              }

              control.updateValueAndValidity()
            })

            this.subscriptions.push(subscription)
          }
        }
      }

      if (element.requiredWhen) {
        for (const prop in element.requiredWhen) {
          const dependant = getDependantControl(prop, control)
          if (dependant) {
            const comparators = getConditionComparators(element.requiredWhen[prop])
            const subscription = dependant.valueChanges.subscribe((value) => {
              const compareResult = comparators.some((comparator) => !!comparator(value))
              if (compareResult) {
                control.addValidators(Validators.required)
              } else {
                control.removeValidators(Validators.required)
              }

              control.updateValueAndValidity()
            })

            this.subscriptions.push(subscription)
          }
        }
      }

      if (element.notRequiredWhen) {
        for (const prop in element.notRequiredWhen) {
          const dependant = getDependantControl(prop, control)
          if (dependant) {
            const comparators = getConditionComparators(element.notRequiredWhen[prop])
            const subscription = dependant.valueChanges.subscribe((value) => {
              const compareResult = comparators.some((comparator) => !!comparator(value))
              if (compareResult) {
                control.removeValidators(Validators.required)
              } else {
                control.addValidators(Validators.required)
              }

              control.updateValueAndValidity()
            })

            this.subscriptions.push(subscription)
          }
        }
      }

      if (element.valuesWhen && codeValueMap) {
        for (const prop in element.valuesWhen) {
          const dependant = getDependantControl(prop, control)
          if (dependant) {
            const comparators = getConditionComparators(element.valuesWhen[prop])
            const subscription = dependant.valueChanges.subscribe((value) => {
              let condition: InsuranceProviderConfiguration.ElementWhenCondition
              for (const comparator of comparators) {
                const result = comparator(value)
                if (!!result) {
                  condition = result
                  break
                }
              }

              if (condition) {
                codeValueMap[key] = condition.values
              } else {
                codeValueMap[key] = element.values
              }
            })

            this.subscriptions.push(subscription)
          }
        }
      }

      // tbd other conditionals

      if (element.required) {
        control.addValidators(Validators.required)
      }

      if (element.min) {
        // tbd
      }

      if (element.max) {
        // tbd
      }

      if (element.minLength) {
        control.addValidators(Validators.minLength(element.minLength))
      }

      if (element.maxLength) {
        // this.maxLengthValidator = Validators.maxLength(this.element.maxLength)
        control.addValidators(Validators.maxLength(element.maxLength))
      }

      if (element.pattern) {
        // this.patternValidator = Validators.pattern(this.element.pattern)
        control.addValidators(Validators.pattern(element.pattern))
      }

      control.updateValueAndValidity()
    }
  }

  private initValueMaps() {
    const codeValueProps = [
      'requestTypeCode',
      'serviceTypeCode',
      'placeOfServiceCode',
      'serviceLevelCode',
      'quantityTypeCode',
      'admissionTypeCode',
      'admissionSourceCode',
      'nursingHomeResidentialStatusCode',
      'transportTypeCode',
      'beginningSubluxationLevelCode',
      'endingSubluxationLevelCode',
      'spinalConditionCode',
      'oxygenDeliverySystemTypeCode',
      'oxygenEquipmentTypeCode',
    ]

    const patientCodeValueProps = [
      'subscriberRelationshipCode',
      'genderCode',
      'statusCode',
      'conditionCode',
      'prognosisCode',
    ]

    const procedureCodeValueProps = [
      'revenueCode',
      'qualifierCode',
      'quantityTypeCode',
    ]

    for (const prop of codeValueProps) {
      if (this.configuration.elements[prop]?.values) {
        this.codeValueMap[prop] = this.configuration.elements[prop].values
      }
    }

    for (const prop of patientCodeValueProps) {
      if (this.configuration.elements.patient?.elements?.[prop]?.values) {
        // this.codeValueMap['patient.' + prop] = this.configuration.elements.patient.elements[prop].values
        this.patientCodeValueMap[prop] = this.configuration.elements.patient.elements[prop].values
      }
    }

    for (const prop of procedureCodeValueProps) {
      if (this.configuration.elements.procedures?.elements?.[prop]?.values) {
        this.proceduresCodeValueMap[prop] = this.configuration.elements.procedures.elements[prop].values
      }
    }

    if (this.configuration.elements.additionalServiceTypes?.elements.code?.values) {
      this.additionalServiceTypesCodeValues = this.configuration.elements.additionalServiceTypes?.elements.code?.values
    }

    // extracting possible role codes for rendering providers other than facility or clinic
    const roleCodeValues: AvailityServiceReview.CodeValue[] = this.configuration.elements.renderingProviders?.elements.roleCode?.values
    if (roleCodeValues) {
      this.otherRenderingProviderRoleCodes = roleCodeValues.filter((value) =>
        value.code !== AvailityServiceReview.RenderingProviderRoleCode.Clinic &&
        value.code !== AvailityServiceReview.RenderingProviderRoleCode.Facility,
      )
    }
  }

  private getProcedureFormGroup() {
    const formGroup = new FormGroup({
      revenueCode: new FormControl(),
      qualifierCode: new FormControl(this.proceduresCodeValueMap.qualifierCode?.[0].code),
      code: new FormControl(),
      modifierCode1: new FormControl(),
      modifierCode2: new FormControl(),
      modifierCode3: new FormControl(),
      modifierCode4: new FormControl(),
      description: new FormControl(),
      quantity: new FormControl(),
      quantityTypeCode: new FormControl(this.proceduresCodeValueMap.quantityTypeCode?.[0].code),
      fromDate: new FormControl(),
      toDate: new FormControl(),
    })

    removeIrrelevantControls(formGroup, this.configuration.elements.procedures.elements)
    this.initStateAndValidators(formGroup, this.configuration.elements.procedures.elements, this.proceduresCodeValueMap)

    return formGroup
  }

  private getRenderingProviderFormGroup() {
    const formGroup = new FormGroup({
      roleCode: new FormControl<string>(''),
      ...getNameControls(),
      ...getLocationControls(),
      // add explicit required validator instead of ugly requiredFieldCombinations handling
      npi: new FormControl('', Validators.required),
      taxId: new FormControl('', Validators.required),
      payerAssignedProviderId: new FormControl(),
      specialtyCode: new FormControl(),
      contactName: new FormControl(),
      phone: new FormControl(),
      extension: new FormControl(),
      fax: new FormControl(),
      emailAddress: new FormControl(),
      submitterId: new FormControl(),
      url: new FormControl(),
    })

    removeIrrelevantControls(formGroup, this.configuration.elements.renderingProviders.elements)
    this.initStateAndValidators(formGroup, this.configuration.elements.renderingProviders.elements)

    return formGroup
  }

  private getProviderNoteFormGroup() {
    const formGroup = new FormGroup({
      message: new FormControl(),
    })

    removeIrrelevantControls(formGroup, this.configuration.elements.providerNotes.elements)
    this.initStateAndValidators(formGroup, this.configuration.elements.providerNotes.elements)

    return formGroup
  }

  private getDiagnosisFormGroup() {
    const formGroup = new FormGroup({
      qualifierCode: new FormControl(),
      code: new FormControl(),
      date: new FormControl(),
    })

    removeIrrelevantControls(formGroup, this.configuration.elements.diagnoses.elements)
    this.initStateAndValidators(formGroup, this.configuration.elements.diagnoses.elements)

    return formGroup
  }

  private getAdditionalServiceTypeFormGroup() {
    const formGroup = new FormGroup({
      code: new FormControl(),
    })

    removeIrrelevantControls(formGroup, this.configuration.elements.additionalServiceTypes.elements)
    this.initStateAndValidators(formGroup, this.configuration.elements.additionalServiceTypes.elements)

    return formGroup
  }
}
